export interface QrCodeOptions {
  width: number;
  height: number;
  type: "canvas" | "svg";
  data: string;
  margin: number;
  color: {
    dark: string;
    light: string;
  };
  errorCorrectionLevel: "L" | "M" | "Q" | "H";
}

export const createQRCode = async (url: string): Promise<HTMLCanvasElement> => {
  if (typeof window === "undefined") {
    throw new Error("QR code generation is only available in the browser");
  }

  try {
    // Use dynamic import for modern browser compatibility
    const QRCodeStyling = (await import("qr-code-styling")).default;
    const PGA_LOGO_BLUE = "https://res.cloudinary.com/pgahq/image/upload/v1695259568/PGAA-blue_2x.png";

    const qrCode = new QRCodeStyling({
      width: 1024,
      height: 1024,
      type: "canvas",
      data: url,
      margin: 40,
      qrOptions: {
        typeNumber: 0,
        mode: "Byte",
        errorCorrectionLevel: "H"
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.5,
        margin: 0
      },
      dotsOptions: {
        type: "rounded",
        color: "#000000"
      },
      backgroundOptions: {
        color: "#ffffff"
      },
      image: PGA_LOGO_BLUE,
      cornersSquareOptions: {
        type: "rounded",
        color: "#000000"
      },
      cornersDotOptions: {
        type: "rounded",
        color: "#000000"
      }
    });

    // Create a temporary div to render the QR code
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.style.top = "-9999px";
    document.body.appendChild(tempDiv);

    // Append the QR code to the temporary div
    qrCode.append(tempDiv);

    // Wait for the QR code to render
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Get the canvas from the rendered QR code
    const renderedCanvas = tempDiv.querySelector("canvas");

    // Clean up the temporary div
    document.body.removeChild(tempDiv);

    if (renderedCanvas) {
      return renderedCanvas as HTMLCanvasElement;
    } else {
      throw new Error("Failed to render QR code");
    }
  } catch (error) {
    console.error("Error generating QR code:", error);
    throw new Error("Failed to generate QR code");
  }
};

export const downloadQRCode = async (url: string, filename: string = "qr-code"): Promise<void> => {
  try {
    const canvas = await createQRCode(url);

    // Download the canvas as PNG
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } catch (error) {
    console.error("Error generating QR code:", error);
    throw error;
  }
};
